import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileSignature, Pencil, Plus, Send, Save, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from './index';
import {
  getPoortwachterDocumenten,
  slaPoortwachterDocumentOp,
  type PoortwachterDocumentProfessional,
} from '../services/medewerkerApi';

interface DocForm {
  id: string | null;
  soort: string;
  titel: string;
  inhoud: string;
}

const LEEG_FORM: DocForm = { id: null, soort: '', titel: '', inhoud: '' };

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('nl-NL') : '');

const StatusChip: React.FC<{ doc: PoortwachterDocumentProfessional }> = ({ doc }) => {
  const { t } = useTranslation('professional');
  switch (doc.status) {
    case 'ondertekend':
      return (
        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full bg-emerald-100 text-emerald-800">
          {t('poortwachter.statusOndertekend', 'Ondertekend {{date}}', { date: fmtDate(doc.ondertekend_op) })}
          {doc.ondertekening_telefoon_suffix && (
            <span className="ml-1 font-mono">(•••• {doc.ondertekening_telefoon_suffix})</span>
          )}
        </span>
      );
    case 'werkgever_getekend':
      return (
        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
          {t('poortwachter.statusWerkgeverGetekend', 'Werkgever getekend · wacht op SMS-akkoord medewerker')}
        </span>
      );
    case 'ter_ondertekening':
      return (
        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
          {t('poortwachter.statusTerOndertekening', 'Vrijgegeven · wacht op handtekening werkgever')}
        </span>
      );
    default:
      return (
        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
          {t('poortwachter.statusConcept', 'Concept')}
        </span>
      );
  }
};

/**
 * Wet Verbetering Poortwachter: documenten (probleemanalyse, plan van aanpak,
 * bijstelling, ...) die de arbo-professional opstelt en ter anonieme
 * SMS-ondertekening aan de medewerker vrijgeeft. Alleen concepten zijn bewerkbaar.
 */
export const PoortwachterDocumenten: React.FC<{ dossierId: string }> = ({ dossierId }) => {
  const { t } = useTranslation('professional');
  const queryClient = useQueryClient();
  const [form, setForm] = useState<DocForm>(LEEG_FORM);
  const [toonForm, setToonForm] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);

  const queryKey = ['poortwachter-documenten', dossierId];
  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: () => getPoortwachterDocumenten(dossierId),
    enabled: !!dossierId,
  });

  const soorten = data?.soorten ?? {};
  const documenten = data?.documenten ?? [];

  const opslaan = useMutation({
    mutationFn: (vrijgeven: boolean) =>
      slaPoortwachterDocumentOp(dossierId, {
        id: form.id,
        soort: form.soort,
        titel: form.titel.trim(),
        inhoud: form.inhoud,
        vrijgeven,
      }),
    onSuccess: (_doc, vrijgeven) => {
      toast.success(
        vrijgeven
          ? t('poortwachter.vrijgegeven', 'Document vrijgegeven ter ondertekening')
          : t('poortwachter.conceptOpgeslagen', 'Concept opgeslagen')
      );
      setForm(LEEG_FORM);
      setToonForm(false);
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.message || t('poortwachter.opslaanFout', 'Opslaan mislukt')),
  });

  const valideer = (): boolean => {
    if (!form.soort) {
      toast.error(t('poortwachter.soortVerplicht', 'Kies een documentsoort'));
      return false;
    }
    if (!form.titel.trim()) {
      toast.error(t('poortwachter.titelVerplicht', 'Titel is verplicht'));
      return false;
    }
    if (!form.inhoud.trim()) {
      toast.error(t('poortwachter.inhoudVerplicht', 'Inhoud is verplicht'));
      return false;
    }
    return true;
  };

  const bewerk = (doc: PoortwachterDocumentProfessional) => {
    setForm({ id: doc.id, soort: doc.soort, titel: doc.titel, inhoud: doc.inhoud });
    setToonForm(true);
  };

  const nieuw = () => {
    setForm({ ...LEEG_FORM, soort: Object.keys(soorten)[0] ?? '' });
    setToonForm(true);
  };

  const annuleer = () => {
    setForm(LEEG_FORM);
    setToonForm(false);
  };

  const vrijgeven = () => {
    if (!valideer()) return;
    if (
      window.confirm(
        t(
          'poortwachter.vrijgevenConfirm',
          'De medewerker ontvangt dit document in het portaal en ondertekent anoniem via SMS-akkoord. Na vrijgeven is het document niet meer te bewerken. Doorgaan?'
        )
      )
    ) {
      opslaan.mutate(true);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-4">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('poortwachter.laden', 'Documenten laden…')}
      </div>
    );
  }

  if (isError) {
    return (
      <p className="text-sm text-red-600 flex items-center gap-2">
        <AlertTriangle className="w-4 h-4" />
        {(error as any)?.response?.data?.message || t('poortwachter.laadFout', 'Poortwachter-documenten konden niet worden geladen.')}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start gap-3 flex-wrap">
        <p className="text-sm text-gray-600 max-w-2xl">
          {t(
            'poortwachter.intro',
            'Stel hier de Poortwachter-documenten op (probleemanalyse, plan van aanpak, bijstellingen). Concepten zijn alleen voor u zichtbaar; na vrijgeven ondertekent de medewerker anoniem via SMS-akkoord in het medewerkersportaal.'
          )}
        </p>
        {!toonForm && (
          <Button size="sm" onClick={nieuw}>
            <Plus className="w-4 h-4 mr-2" />
            {t('poortwachter.nieuwDocument', 'Nieuw document')}
          </Button>
        )}
      </div>

      {toonForm && (
        <form
          className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (valideer()) opslaan.mutate(false);
          }}
        >
          <h3 className="text-sm font-semibold text-gray-900">
            {form.id
              ? t('poortwachter.conceptBewerken', 'Concept bewerken')
              : t('poortwachter.nieuwDocument', 'Nieuw document')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={labelClass} htmlFor="pw-soort">{t('poortwachter.soort', 'Documentsoort')}</label>
              <select
                id="pw-soort"
                className={inputClass}
                value={form.soort}
                onChange={(e) => setForm((p) => ({ ...p, soort: e.target.value }))}
              >
                <option value="">{t('poortwachter.kiesSoort', 'Kies een soort…')}</option>
                {Object.entries(soorten).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass} htmlFor="pw-titel">{t('poortwachter.titel', 'Titel')}</label>
              <input
                id="pw-titel"
                type="text"
                className={inputClass}
                value={form.titel}
                onChange={(e) => setForm((p) => ({ ...p, titel: e.target.value }))}
                placeholder={t('poortwachter.titelPh', 'bijv. Probleemanalyse week 6')}
              />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor="pw-inhoud">{t('poortwachter.inhoud', 'Inhoud')}</label>
            <textarea
              id="pw-inhoud"
              rows={14}
              className={`${inputClass} font-mono`}
              value={form.inhoud}
              onChange={(e) => setForm((p) => ({ ...p, inhoud: e.target.value }))}
              placeholder={t('poortwachter.inhoudPh', 'De volledige tekst van het document zoals de medewerker die te zien krijgt.')}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="submit" variant="outline" size="sm" loading={opslaan.isPending}>
              <Save className="w-4 h-4 mr-2" />
              {t('poortwachter.opslaanConcept', 'Opslaan als concept')}
            </Button>
            <Button type="button" size="sm" onClick={vrijgeven} disabled={opslaan.isPending}>
              <Send className="w-4 h-4 mr-2" />
              {t('poortwachter.vrijgeven', 'Vrijgeven ter ondertekening')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={annuleer} disabled={opslaan.isPending}>
              {t('poortwachter.annuleren', 'Annuleren')}
            </Button>
          </div>
        </form>
      )}

      {documenten.length === 0 ? (
        <p className="text-sm text-gray-500">{t('poortwachter.geenDocumenten', 'Nog geen Poortwachter-documenten voor dit dossier.')}</p>
      ) : (
        <ul className="space-y-2">
          {documenten.map((doc) => {
            const open = openId === doc.id;
            return (
              <li key={doc.id} className="rounded-lg border border-gray-200 bg-white">
                <div className="flex items-start justify-between gap-3 p-3">
                  <button
                    type="button"
                    className="flex-1 text-left min-w-0"
                    onClick={() => setOpenId(open ? null : doc.id)}
                    aria-expanded={open}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileSignature className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="text-xs font-medium text-gray-500 uppercase">{soorten[doc.soort] || doc.soort}</span>
                      <StatusChip doc={doc} />
                    </div>
                    <div className="mt-1 font-semibold text-gray-900 truncate">{doc.titel}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {t('poortwachter.aangemaakt', 'Aangemaakt {{date}}', { date: fmtDate(doc.created_at) })}
                      {doc.vrijgegeven_op && ` · ${t('poortwachter.vrijgegevenOp', 'vrijgegeven {{date}}', { date: fmtDate(doc.vrijgegeven_op) })}`}
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {doc.status === 'concept' && (
                      <Button variant="outline" size="sm" onClick={() => bewerk(doc)}>
                        <Pencil className="w-4 h-4 mr-1" />
                        {t('poortwachter.bewerken', 'Bewerken')}
                      </Button>
                    )}
                    <button
                      type="button"
                      className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
                      onClick={() => setOpenId(open ? null : doc.id)}
                      aria-label={open ? t('poortwachter.inklappen', 'Inklappen') : t('poortwachter.uitklappen', 'Uitklappen')}
                    >
                      {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                {open && (
                  <div className="border-t border-gray-200 p-3 bg-gray-50">
                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800">{doc.inhoud}</pre>
                    {doc.status === 'ondertekend' && doc.ondertekening_hash && (
                      <p className="mt-3 text-xs text-gray-500 font-mono break-all">
                        {t('poortwachter.hash', 'Ondertekeningshash')}: {doc.ondertekening_hash}
                      </p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
